/**
 * Character WebSocket Event Hook
 *
 * File: Mythbound.App/src/hooks/useCharacterWebSocket.ts
 * Created: 2026-01-06
 * Last Modified: 2026-01-06
 *
 * PURPOSE:
 * React hook for subscribing to character-related WebSocket events.
 * Integrates with CharacterContext to update state in real-time.
 *
 * EVENTS HANDLED (INSTANT):
 * - character.created - Character added to roster
 * - character.deleted - Character removed/archived
 *
 * POLLED STATE (3-second interval):
 * - character.hp_updated - HP changes (uses polling, not instant)
 * - character.visibility_updated - Visibility settings changed (uses polling)
 * (Handled by SessionContext polling, not WebSocket)
 *
 * USAGE:
 * ```tsx
 * function CampaignPage() {
 *   const { currentCampaign } = useCampaign();
 *   useCharacterWebSocket(currentCampaign?.campaign_id);
 *   // Component automatically updates on WebSocket events
 * }
 * ```
 *
 * Pipeline: WebSocket events → useCharacterWebSocket → CharacterContext → UI updates
 * Dependencies: React, websocketService, useCharacter context
 */

import { useEffect, useRef } from 'react';
import { useCharacter } from '../context/CharacterContext';
import { toastService } from '../services/toast/toastService';
import { webSocketService } from '../services/websocket/websocketService';

export const useCharacterWebSocket = (campaignId?: string) => {
  const { loadRoster } = useCharacter();

  // Store handlers in refs to avoid re-subscribing on every render
  const handlersRef = useRef<Record<string, (data: any) => void>>({});

  useEffect(() => {
    // Skip if no campaign ID
    if (!campaignId) return;

    /**
     * Handle character.created event (INSTANT)
     * Triggered when a new character is added to the campaign
     */
    handlersRef.current['character.created'] = (data: any) => {
      console.log('[Character WS] character.created:', data);
      if (data.campaign_id === campaignId) {
        // Reload roster to include new character
        loadRoster(campaignId);

        toastService.success(
          `${data.creator_name || 'A player'} created character: ${data.character_name}`
        );
        
        // TODO: Animate new character appearing in roster sidebar
      }
    };

    /**
     * Handle character.deleted event (INSTANT)
     * Triggered when a character is deleted/archived
     */
    handlersRef.current['character.deleted'] = (data: any) => {
      console.log('[Character WS] character.deleted:', data);
      if (data.campaign_id === campaignId) {
        // Reload roster to remove deleted character
        loadRoster(campaignId);

        toastService.info(`Character ${data.character_name} was removed`);
      }
    };

    /**
     * NOTE: HP updates and visibility changes use POLLING (3-second interval)
     * These are non-critical updates that don't need instant WebSocket events.
     * SessionContext polling handles these via getSessionState() endpoint.
     * 
     * If instant updates are needed in the future, uncomment below:
     */

    // handlersRef.current['character.hp_updated'] = (data: any) => {
    //   console.log('[Character WS] character.hp_updated:', data);
    //   if (data.campaign_id === campaignId) {
    //     // Update specific character HP in roster
    //     updateCharacterHP(data.character_id, data.hp, data.max_hp);
    //   }
    // };

    // handlersRef.current['character.visibility_updated'] = (data: any) => {
    //   console.log('[Character WS] character.visibility_updated:', data);
    //   if (data.campaign_id === campaignId) {
    //     // Reload roster to get updated visibility settings
    //     loadRoster(campaignId);
    //   }
    // };

    // Subscribe to all events
    Object.entries(handlersRef.current).forEach(([event, handler]) => {
      webSocketService.on(event, handler);
    });

    // Cleanup: unsubscribe from all events
    return () => {
      Object.entries(handlersRef.current).forEach(([event, handler]) => {
        webSocketService.off(event, handler);
      });
    };
  }, [campaignId, loadRoster]);
};

